/**
 * Utilitário de identificação de tenant para o frontend.
 *
 * Hierarquia de resolução do slug:
 *   1. localStorage.getItem('tenantSlug')  — salvo no login ou troca de empresa
 *   2. Subdomínio da URL (produção)         — cliente1.salesflow.com.br → "cliente1"
 *   3. VUE_APP_TENANT_SLUG                  — variável de build (.env)
 *   4. 'dkp'                               — fallback final
 */

/**
 * Retorna o slug do tenant ativo.
 * @returns {string}
 */
export function getTenantSlug() {
  const fromStorage = localStorage.getItem('tenantSlug');
  if (fromStorage) return fromStorage;

  const hostname = window.location.hostname;

  if (hostname !== 'localhost' && hostname !== '127.0.0.1' && !hostname.includes('.replit.dev')) {
    const parts = hostname.split('.');
    if (parts.length >= 3) return parts[0];
  }

  return process.env.VUE_APP_TENANT_SLUG || 'dkp';
}

/**
 * Define o slug do tenant e persiste no localStorage.
 * Chamado pelo login ou pelo painel de alternância de empresa.
 * @param {string} slug
 */
export function setTenantSlug(slug) {
  const clean = (slug || 'dkp').toLowerCase().trim();
  localStorage.setItem('tenantSlug', clean);
}

/**
 * Inicia impersonação de tenant pelo AdminDKP.
 * A autenticação continua usando o banco DKP; os dados são lidos do banco alvo.
 * Sempre garante que tenantSlug de auth seja 'dkp'.
 * @param {string} slug  Slug do tenant a ser impersonado
 */
export function setImpersonateTenant(slug) {
  // Auth slug deve ser sempre 'dkp' — remove qualquer estado legado
  localStorage.setItem('tenantSlug', 'dkp');
  if (!slug || slug === 'dkp') {
    localStorage.removeItem('dkpImpersonateSlug');
  } else {
    localStorage.setItem('dkpImpersonateSlug', slug.toLowerCase().trim());
  }
}

/**
 * Retorna o slug do tenant sendo impersonado, ou null se não há impersonação ativa.
 * @returns {string|null}
 */
export function getImpersonateTenant() {
  return localStorage.getItem('dkpImpersonateSlug') || null;
}

/**
 * Encerra a impersonação e volta ao contexto normal do DKP.
 */
export function clearImpersonation() {
  localStorage.removeItem('dkpImpersonateSlug');
}

/**
 * Retorna a configuração básica do tenant atual.
 * @returns {{ slug: string, baseUrl: string }}
 */
export function getTenantConfig() {
  return {
    slug: getTenantSlug(),
    baseUrl: window.location.origin,
  };
}
